import _set from 'lodash/set';
import { BaseChartProperty } from '../../base-property';
import type { DataListItem } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series';
import type {
    DataPoint,
    IChartMarker,
    IChartSeries,
    IDataLabel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import { getFormattedNumber } from '@src/slide/domain/ui-model/element-modeles/property/chart/util/format';
import type {
    IShapeModelProps,
} from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/shape-model-props.interface';
import { MarkerProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/geometry/marker';
import { STMarkerStyleDmlC, SubChartType } from '../../../../Interface/chart-v3/sdk-model.interface';
import { IBubbleChartDataItemProperty, IChartDataItem, IDataItemProperty } from './data-item.interface';
import { IDataLabelProperty } from '../data-label/data-label.interface';
import {
    mergeStyleProps,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/util/style';
import isNil from 'lodash/isNil';
import { Nullable, NullableDeep } from '../../chart-shape-property.interface';
import { ShapeProps } from '../../../../Interface/chart-v3/chart-properties.interface';
import { IGeometryProperty } from '../geometry/geometry.interface';
import { GeometryProperty } from '../geometry';
import { DEFAULT_CATEGORY } from '../of-pie-chart.interface';
import { shallowMerge } from '@src/slide/domain/ui-model/element-modeles/property/chart/util/merge';
import ChartDefaultValueFactory from '../../../../chart-models/chart-presets/default-value-factory';
import { DataLabel } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/properties/data-label';

export class DataItemProperty extends BaseChartProperty<DataListItem> implements IDataItemProperty {
    public id: string;
    public type: SubChartType;
    public category: string | number;
    public originCategory: string | number | undefined;
    public categoryIndex: number;
    public categoryFormatCode: string | undefined;
    public value: number;
    public originValue: string | number | undefined;
    public valueFormatCode: string | undefined;
    public ser: IChartSeries;
    public serIndex: number;
    public props: IShapeModelProps;
    public marker: MarkerProperty | undefined;
    public dataPoint?: DataPoint;
    public dataLabel?: DataLabel;
    public label?: IDataLabelProperty;
    public geometry: IGeometryProperty;
    public autoThemeColor?: boolean;

    // eslint-disable-next-line complexity
    public init(param: {
        dataItem: DataListItem,
        index: number,
        serItem: IChartSeries,
        serIndex: number,
    }) {
        const { dataItem, serItem, serIndex, index } = param;
        const { type } = this.context.getSubChartModel();
        const { dateAxes } = this.context.getChartModel().plotArea;
        const hasDateAxis = !!dateAxes?.length
            && serItem.dataList.every(data => !isNaN(Number(data.categoryPoint?.value)));

        const originCategory = dataItem.categoryPoint?.value;
        const isCategorySortable = hasDateAxis || typeof originCategory === 'number';
        const isCategoryNaN = isNaN(Number(originCategory));
        const category = (isCategorySortable && !isCategoryNaN) ? Number(originCategory) : `${originCategory || ''}`;

        const originValue = dataItem.valuePoint?.value;
        const value = getFormattedNumber(originValue) as number;

        this.id = dataItem.id;
        this.type = type;
        this.category = category;
        this.originCategory = originCategory;
        this.categoryIndex = index;
        this.categoryFormatCode = dataItem.categoryPoint?.formatCode || serItem.categoryFormatCode;
        this.value = value;
        this.originValue = originValue;
        this.valueFormatCode = dataItem.valuePoint?.formatCode || serItem.valueFormatCode;
        this.ser = serItem;
        this.serIndex = serIndex;
        this.props = serItem.props as IShapeModelProps;
        this.marker = serItem.marker && new MarkerProperty(serItem.marker, this.context).init(this);

        // 处理数据点自身的dataPoint属性，需要和ser的属性做合并
        if (dataItem.dataPoint) {
            // 挂载dataPoint，用于外界知道有dataPoint属性
            this.dataPoint = dataItem.dataPoint;
            // 1. merge ser props
            this.props = mergeStyleProps(serItem.props, dataItem.dataPoint.props as IShapeModelProps);
            // 2. merge marker
            this.marker = new MarkerProperty(
                shallowMerge(serItem.marker, dataItem.dataPoint.marker),
                this.context,
            ).init(this);
        }

        // 处理数据点的dataLabel属性
        const isDataItemValueEmpty = originValue === '' || isNil(originValue) || isNil(value);
        const isLabelDeleted = dataItem.dataLabel?.delete;
        const isDisplayDataLabel = !isDataItemValueEmpty && !isLabelDeleted && serItem.dataLabels;
        // 标签被删除 或者 原始值异常的时候不展示数据标签
        if (isDisplayDataLabel) {
            this.dataLabel = dataItem.dataLabel;
            const subChartProperty = this.context.getSubChartModel().getSubChartProperty();
            this.label = subChartProperty.getDataLabelProperty(
                shallowMerge(serItem.dataLabels, { ...dataItem.dataLabel }),  // 这里child必须是新对象，否则会把model数据给修改掉
                this,
            );
        }

        if (this.uiData.dataPoint) {
            this.autoThemeColor = this.subChartModel.getSubChartProperty()
                .isAutoThemeColor(this.uiData.dataPoint, index, serItem.dataList.length);
        }
        this.geometry = new GeometryProperty({ props: this.props }, this.context).init(this);
        return this;
    }

    public updateDataLabel(props: Partial<Nullable<IDataLabel>>) {
        let updateDataItem;
        let removeDataItem;
        if (this.id === DEFAULT_CATEGORY) {
            const update = this.ser.otherDLbl ? {
                otherDLbl: props,
            } : {
                otherDLbl: {
                    ...this.ser.dataLabels,
                    ...props,
                },
            };
            const remove = Object.keys(props).includes('labelPosition') ? {
                otherDLbl: {
                    layout: null,
                },
            } : undefined;
            this.updateSeriesProps(update, remove);
            return;
        }
        if (!this.uiData.dataLabel) {
            updateDataItem = {
                dataLabel: {
                    ...this.ser.dataLabels,
                    ...props,
                },
            };
        } else {
            updateDataItem = {
                dataLabel: props,
            };
        }
        // 如果修改了位置， 需要删掉layout
        if (Object.keys(props).includes('labelPosition')) {
            removeDataItem = {
                dataLabel: { layout: null },
            };
        }
        this.updateProps(updateDataItem, removeDataItem);
    }

    public updateGeometry(updateProps: Partial<ShapeProps>, removeProps?: Partial<NullableDeep<ShapeProps>>) {
        if (this.id === DEFAULT_CATEGORY) {
            this.updateSeriesProps({
                otherDPt: updateProps,
            }, {
                otherDPt: removeProps,
            });
            return;
        }
        let updateDataItem;
        // 原来的dataPoint下没有props, 使用series props创建
        if (!this.uiData.dataPoint?.props) {
            updateDataItem = {
                dataPoint: {
                    props: {
                        style: {
                            ...this.ser?.props?.style,
                            ...updateProps?.props?.style,
                        },
                    },
                },
            };
        } else {
            updateDataItem = {
                dataPoint: updateProps,
            };
        }
        const removeDataItem = {
            dataPoint: removeProps,
        };

        this.updateProps(updateDataItem, removeDataItem);
    }

    public updateProps(updateProps: Partial<DataListItem>, removeProps?: Partial<NullableDeep<DataListItem>>) {
        const updateDataList = [...new Array(this.categoryIndex), updateProps];
        const removeDataList = [...new Array(this.categoryIndex), removeProps];
        const updateSeries = [...new Array(this.serIndex), {
            dataList: updateDataList,
        }];
        const removeSeries = [...new Array(this.serIndex), {
            dataList: removeDataList,
        }];

        this.subChartModel.updateAndRemoveProps(
            { series: updateSeries },
            { series: removeSeries },
        );
    }

    public updateSeriesProps(updateProps, removeProps?) {
        const updateSeries = updateProps ? [...new Array(this.serIndex), updateProps] : updateProps;
        const removeSeries = removeProps ? [...new Array(this.serIndex), removeProps] : removeProps;

        this.subChartModel.updateAndRemoveProps(
            { series: updateSeries },
            { series: removeSeries },
        );
    }

    public updateMarker(updateProps: Partial<IChartMarker>, removeProps?: Partial<NullableDeep<IChartMarker>>) {
        let updateDataItem;
        // 原来的dataPoint下没有marker, 使用series marker创建
        if (!this.uiData.dataPoint?.marker) {
            updateDataItem = {
                dataPoint: {
                    marker: {
                        ...this.ser?.marker,
                        ...updateProps,
                        props: {
                            style: {
                                ...this.ser?.marker?.props.style,
                                ...updateProps?.props?.style,
                            },
                        },
                    },
                },
            };
        } else {
            updateDataItem = {
                dataPoint: {
                    marker: updateProps,
                },
            };
        }
        const removeDataItem = {
            dataPoint: {
                marker: removeProps,
            },
        };

        this.updateProps(updateDataItem, removeDataItem);
    }

    public createMarker(symbol: STMarkerStyleDmlC) {
        const props = {};
        _set(props, 'style.border.color.colorValue', `accent${this.serIndex! + 1}`);
        this.updateMarker({
            ...ChartDefaultValueFactory.createDefaultMarker({
                props,
            }),
            symbol,
        });
    }

    public getGeometryProperty(showMarker: boolean) {
        return showMarker ? this.marker : this.geometry;
    }

    private get subChartModel() {
        return this.context.getSubChartModel();
    }
}

export class BubbleChartDataItemProperty extends DataItemProperty implements IBubbleChartDataItemProperty {
    public size: number;
    public originSize: number;

    public init(param: {
        dataItem: DataListItem,
        index: number,
        serItem: IChartSeries,
        serIndex: number,
        size: number,
        originSize: number,
    }) {
        // 先设置上originSize，在基类中会统一处理originSize相关的数据标签内容
        this.size = param.size;
        this.originSize = param.originSize;

        super.init(param);

        return this;
    }
}
